import { useForm } from "@mantine/form"
import React, { useState } from "react"
import { checkDataExistence, createClient } from "../api"
import { confirmDiscardChanges } from "../utils/confirm"
import { notifications } from "@mantine/notifications"
import { BaseModal, requiredMark, requireLabelStyles } from "./BaseModal"
import { Box, Button, Group, PasswordInput, Radio, ScrollArea, Stack, Text, TextInput } from "@mantine/core"

const EMAIL_REGEXP = /^[A-Za-z0-9]{1}[A-Za-z0-9_.+-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/

const INVY_SERVICE_USE_OPTIONS = [
  { value: '0', label: 'ECなどオンラインのみ' },
  { value: '1', label: '店舗への来店誘導のみ' },
  { value: '2', label: 'オンラインでの予約と店舗への誘導両方' },
]

const PAGE_CREATION_OPTIONS = [
  { value: '0', label: 'カンタンテンプレート' },
  { value: '1', label: 'オリジナルHTML' },
]

type DuplicateField = 'name' | 'urlName' | 'email'

const DUPLICATE_FIELDS: DuplicateField[] = ['name', 'urlName', 'email']

const DUPLICATE_MESSAGES: Record<DuplicateField, string> = {
  name: 'クライアント名が既に存在します。',
  urlName: 'クライアントURLが既に存在します。',
  email: 'メールアドレスが既に存在します。',
}

const validateRequired255 = (value: string): string | null => {
  if (!value) return '必須です'
  if (value.length > 255) return '255文字以下で入力してください'
  return null
}

const validateEmail = (value: string): string | null => {
  if (!value) return '必須です'
  if (!EMAIL_REGEXP.test(value)) return '正しいメールアドレスを入力してください'
  return null
}

const validatePassword = (value: string): string | null => {
  if (!value) return '必須です'
  if (value.length > 16) return '16文字以下で入力してください'
  if (value.length < 8) return '8文字以上で入力してください'
  return null
}

interface Props {
  opened: boolean
  onClose: () => void
  onSaved?: () => void
}

interface FormValues {
  name: string
  invy_service_use: string
  page_creation: string
  urlName: string
  email: string
  password: string
  password_confirmation: string
}

const defaultValues: FormValues = {
  name: '',
  invy_service_use: '',
  page_creation: '',
  urlName: '',
  email: '',
  password: '',
  password_confirmation: '',
}

export const ClientCreateModal: React.FC<Props> = ({ opened, onClose, onSaved }) => {
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<FormValues>({
    initialValues: defaultValues,
    validateInputOnBlur: true,
    validate: {
      name: validateRequired255,
      invy_service_use: (v) => (!v ? '必須です' : null),
      page_creation: (v) => (!v ? '必須です' : null),
      urlName: validateRequired255,
      email: validateEmail,
      password: validatePassword,
      password_confirmation: (v, values) => {
        const error = validatePassword(v)
        if (error) return error
        if (v !== values.password) return 'パスワードが一致しません'
        return null
      },
    },
  })

  // ローカルバリデーションを通らない値はAPIに問い合わせない
  const handleBlurDuplicate = async (field: DuplicateField) => {
    const value = form.values[field]
    if (!value || form.validateField(field).hasError) return

    try {
      if (await checkDataExistence(value, field)) {
        form.setFieldError(field, DUPLICATE_MESSAGES[field])
      }
    } catch (e) {
      console.error('重複チェックに失敗しました', e)
    }
  }

  // getInputProps の onBlur を潰さないよう、両方呼ぶ
  const duplicateProps = (field: DuplicateField) => {
    const inputProps = form.getInputProps(field)
    return {
      ...inputProps,
      onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
        inputProps.onBlur?.(event)
        handleBlurDuplicate(field)
      },
    }
  }

  const handleClose = () => {
    if (submitting) return
    if (form.isDirty() && !confirmDiscardChanges()) return
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)

    try {
      // blur を経ずに送信された場合の取りこぼしを防ぐため、3項目をまとめて再チェックする
      const results = await Promise.all(
        DUPLICATE_FIELDS.map(async (field) => ({
          field,
          exists: await checkDataExistence(values[field], field),
        })),
      )
      const duplicated = results.filter((result) => result.exists)
      if (duplicated.length > 0) {
        duplicated.forEach((result) =>
          form.setFieldError(result.field, DUPLICATE_MESSAGES[result.field])
        )
        return
      }

      await createClient(
        values.name,
        values.urlName,
        values.email,
        values.password,
        values.password_confirmation,
        values.invy_service_use,
        values.page_creation,
      )
      notifications.show({ message: 'クライアントを作成しました', color: 'teal' })
      onSaved?.()
      onClose()
    } catch (e) {
      console.error('クライアントの作成に失敗しました', e)
      notifications.show({ message: 'クライアントの作成に失敗しました', color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title="新規クライアント作成"
      onExited={() => form.reset()}
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
          <Stack gap="md" p="md">
            <TextInput
              label={<><span>クライアント名</span>{requiredMark}</>}
              autoComplete="off"
              {...duplicateProps('name')}
              styles={requireLabelStyles}
            />

            <Radio.Group
              label={<><span>利用するサービスについて</span>{requiredMark}</>}
              {...form.getInputProps('invy_service_use')}
              styles={requireLabelStyles}
            >
              <Stack gap={8} mt={8}>
                {INVY_SERVICE_USE_OPTIONS.map((option) => (
                  <Radio key={option.value} value={option.value} label={option.label} />
                ))}
              </Stack>
            </Radio.Group>

            <Radio.Group
              label={<><span>ページ作成の種類</span>{requiredMark}</>}
              description="※後から変更することはできません"
              {...form.getInputProps('page_creation')}
              styles={requireLabelStyles}
            >
              <Stack gap={8} mt={8}>
                {PAGE_CREATION_OPTIONS.map((option) => (
                  <Radio key={option.value} value={option.value} label={option.label} />
                ))}
              </Stack>
            </Radio.Group>

            <Box>
              <TextInput
                label={<><span>クライアントURL</span>{requiredMark}</>}
                autoComplete="off"
                {...duplicateProps('urlName')}
                styles={requireLabelStyles}
              />
              <Text fz={13} c="#00000099" mt={8}>
                {`https://invy.jp/${form.values.urlName || 'クライアントURL'}/invite/?code=[紹介者会員番号]&name=[紹介者名]`}
              </Text>
            </Box>

            <TextInput
              label={<><span>メールアドレス</span>{requiredMark}</>}
              autoComplete="off"
              {...duplicateProps('email')}
              styles={requireLabelStyles}
            />

            <PasswordInput
              label={<><span>ログインパスワード</span>{requiredMark}</>}
              placeholder="8～16文字"
              autoComplete="new-password"
              {...form.getInputProps('password')}
              styles={requireLabelStyles}
            />

            <PasswordInput
              label={<><span>ログインパスワード（確認）</span>{requiredMark}</>}
              placeholder="8～16文字"
              autoComplete="new-password"
              {...form.getInputProps('password_confirmation')}
              styles={requireLabelStyles}
            />
          </Stack>
        </ScrollArea>

        <Box
          p="md"
          style={{
            borderTop: '1px solid #0000001A',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}
        >
          <Group justify="flex-end" gap={8}>
            <Button
              variant="outline"
              radius="xl"
              color="gray"
              onClick={handleClose}
              disabled={submitting}
            >
              キャンセル
            </Button>
            <Button
              variant="filled"
              color="invy"
              radius="xl"
              type="submit"
              loading={submitting}
            >
              保存
            </Button>
          </Group>
        </Box>
      </form>
    </BaseModal>
  )
}
